import { useCallback, useMemo } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Trans, useTranslation } from 'react-i18next';

import { Theme } from '@app/theme';
import {
  ModeratedTaskFormat,
  ModeratedTaskSetting,
  Participation,
  ParticipationStatus,
  useRestoreToAccepted,
  useSession,
  useTaskMeeting,
  useTaskSetting,
  VideoCallProvider,
} from '@app/api';
import { Button, Chip, TitleWithIcon } from 'components';
import classnames from 'classnames';

import { getFormatIllustration } from '@app/core';
import { AppointmentDetailsVideoCallSection } from './AppointmentDetailsVideoCallSection';

const useStyles = createUseStyles<string, {}, Theme>((theme: Theme) => ({
  formatBlock: {
    display: 'flex',
    alignItems: 'center',

    backgroundColor: theme.colors.grayBackground,
    padding: [[theme.marginBase * 2, theme.marginBase * 3]],
    borderRadius: theme.borderRadius.std,
  },
  illustration: {
    width: theme.marginBase * 8,
    height: theme.marginBase * 8,
    marginRight: theme.marginBase * 3,
  },
  textBlock: {
    flex: 1,
  },
  date: {
    ...theme.fonts.label,
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase / 2,
  },
  timezone: {
    ...theme.fonts.caption,
    marginTop: theme.marginBase / 2,
    color: theme.colors.inkSecondary,
  },
  buttons: {
    marginTop: theme.marginBase * 3,
    marginRight: -theme.marginBase,
  },
  button: {
    marginRight: theme.marginBase,
  },
  caret: {
    fontSize: theme.iconSize.regular,
  },
  caretContainer: {
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: theme.height.small,
    width: 28,
  },
  cancellationContainer: {
    marginTop: theme.marginBase * 3,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase + 'px',
  },
  cancellationFirstLine: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.marginBase + 'px',
    ...theme.fonts.label,
  },
  cancellationReason: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
  },
}));

interface Props {
  participation: Participation;
  className?: string;
  reschedule?: (id: string) => any;
  cancel?: (id: string) => any;
}

const allowedToRescheduleOnly = [
  ParticipationStatus.CANCELED,
  ParticipationStatus.NO_SHOW,
];

export const AppointmentDetails = ({
  participation,
  className,
  reschedule,
  cancel,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  const { data: meeting } = useTaskMeeting(participation.taskId);

  const { mutate: restoreToAccept } = useRestoreToAccepted();
  const restoreToAccepted = async () => {
    restoreToAccept(participation);
  };

  const { data: session } = useSession(participation.sessionId);
  const { data: taskSetting } = useTaskSetting<ModeratedTaskSetting>(
    session?.taskSettingId,
  );
  const participantTimezone = participation.participant?.timezone;
  const selectedDevice = useMemo(
    () => participation?.selectedDevice,
    [participation],
  );

  const onVideoCallClick = useCallback(() => {
    if (!meeting?.videoCallLink) {
      return;
    }
    window.open(meeting?.videoCallLink);
  }, [meeting]);

  if (!meeting?.format) {
    return null;
  }
  const illustration = getFormatIllustration(meeting?.format);

  return (
    <div className={className}>
      <TitleWithIcon
        icon="ri-calendar-event-line"
        iconColor={
          participation.status === ParticipationStatus.CANCELED
            ? 'red'
            : 'green'
        }
        title={t('sessions.details.monitoring.appointment.title')}
      />

      {!!meeting && (
        <div className={classes.formatBlock}>
          <img
            src={illustration}
            alt={t(
              `sessions.details.monitoring.appointment.formats.${meeting.format}`,
            )}
            className={classes.illustration}
          />

          <div className={classes.textBlock}>
            <div className={classes.date}>
              <span>
                <Trans
                  i18nKey="sessions.details.monitoring.appointment.date"
                  values={{
                    start: meeting.start,
                    end: meeting.end,
                  }}
                />
              </span>
              {!!selectedDevice && (
                <Chip color="blue">
                  <span>
                    {t(
                      `sessions.display.allowedDevicesCategoriesChoices.${selectedDevice}`,
                    )}
                  </span>
                </Chip>
              )}
            </div>
            {participantTimezone && (
              <span className={classes.timezone}>
                <Trans
                  i18nKey="sessions.details.monitoring.appointment.participantTimezone"
                  values={{
                    timezone: participantTimezone,
                    start: meeting.start,
                    end: meeting.end,
                  }}
                />
              </span>
            )}
          </div>
        </div>
      )}

      {participation.status === ParticipationStatus.CANCELED && (
        <div className={classes.cancellationContainer}>
          <div className={classes.cancellationFirstLine}>
            <Chip color="red">
              {t('sessions.details.monitoring.chips.cancelled')}
            </Chip>
            {participation.cancelledAppointmentResponsible && (
              <span>
                {t(
                  `sessions.details.monitoring.cancellation.by.${participation.cancelledAppointmentResponsible}`,
                )}
              </span>
            )}
            {participation.cancelledAppointmentReasonGiven && (
              <span>
                {t('sessions.details.monitoring.cancellation.reasonGiven')}
              </span>
            )}
            {!participation.cancelledAppointmentReasonGiven &&
              !participation.cancelledAppointmentResponsible && (
                <span className={classes.cancellationReason}>
                  {t('sessions.details.monitoring.cancellation.noHistory')}
                </span>
              )}
          </div>
          {participation.cancelledAppointmentReasonGiven && (
            <span className={classes.cancellationReason}>
              {participation.cancelledAppointmentReasonGiven}
            </span>
          )}
        </div>
      )}

      {meeting && participation.status === ParticipationStatus.BOOKED && (
        <div className={classes.buttons}>
          {taskSetting?.videoCallProvider === VideoCallProvider.GOOGLE_MEET && (
            <Button
              onClick={onVideoCallClick}
              text={t('sessions.details.monitoring.videoCallLink.joinMeeting')}
              small
              disabled={!meeting.videoCallLink}
              rightInsert={
                <div className={classes.caretContainer}>
                  <i
                    className={classnames('ri-share-box-line', classes.caret)}
                  />
                </div>
              }
            />
          )}
          {!!reschedule && (
            <Button
              color="blue"
              variant="transparent"
              text={t('sessions.details.monitoring.appointment.reschedule')}
              small
              className={classes.button}
              onClick={() => reschedule(participation.id)}
            />
          )}
          {!!cancel && (
            <Button
              color="red"
              variant="transparent"
              text={t('sessions.details.monitoring.appointment.cancel')}
              small
              className={classes.button}
              onClick={() => cancel(participation.id)}
            />
          )}
        </div>
      )}
      {allowedToRescheduleOnly.includes(participation.status) &&
        !!reschedule && (
          <div className={classes.buttons}>
            {(participation.status === ParticipationStatus.CANCELED ||
              participation.status === ParticipationStatus.NO_SHOW) && (
              <Button
                color="blue"
                text={t(
                  'sessions.details.monitoring.screening.restoreToAccept',
                )}
                small
                className={classes.button}
                onClick={restoreToAccepted}
              />
            )}
            <Button
              color="blue"
              variant="transparent"
              text={t(
                'sessions.details.monitoring.appointment.reschedule_canceled',
              )}
              small
              className={classes.button}
              onClick={() => reschedule(participation.id)}
            />
          </div>
        )}

      {participation.status === ParticipationStatus.BOOKED &&
        meeting?.format === ModeratedTaskFormat.VIDEO_CALL && (
          <AppointmentDetailsVideoCallSection
            participation={participation}
            meeting={meeting}
          />
        )}
    </div>
  );
};
